/**
 * Who can actually be reached on a channel, and who cannot, with the reason.
 *
 * Two different things stop a send, and telling them apart matters. Somebody
 * with no phone number is a data gap the author can fix. Somebody who sent STOP
 * is a decision that is theirs, and no amount of fixing changes it.
 */

import { channelName } from '../utils/channelCatalogue';

/**
 * The address this channel would use to reach somebody.
 *
 * A recipient can hold several. Picking the wrong one sends a WhatsApp message
 * to an email address, which fails late and looks like a provider fault.
 */
export function addressFor(recipient, channel) {
    if (!recipient) return '';
    let raw;
    if (channel === 'email') raw = recipient.email;
    else if (channel === 'sms' || channel === 'whatsapp') raw = recipient.phone_number;
    else raw = recipient.channel_address;
    return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Split a recipient list into who can be reached and who cannot.
 *
 * `doNotContact` being missing is not the same as it being empty, and the
 * difference is the whole point of this function. An empty list means nobody has
 * withdrawn. A missing list means the question was never answered.
 *
 * A send that cannot check whether somebody withdrew must not go out. So a
 * missing list blocks everybody. This is the same rule the dispatcher applies,
 * and it is deliberate: the cost of refusing a send is a delay, and the cost of
 * sending to somebody who said stop is a broken promise and a complaint to a
 * platform that can close the account.
 */
export function assessEligibility(recipients = [], doNotContact, channel) {
    const list = Array.isArray(recipients) ? recipients : [];
    const eligible = [];
    const blocked = [];

    if (doNotContact === null || doNotContact === undefined) {
        const reason = 'The do-not-contact list could not be read, so this send is refused.';
        for (const recipient of list) blocked.push({ recipient, reason });
    } else {
        const withdrawn = new Set(
            (Array.isArray(doNotContact) ? doNotContact : [])
                .filter((e) => e && e.channel === channel && typeof e.recipient_identity === 'string')
                .map((e) => e.recipient_identity.trim().toLowerCase())
        );

        for (const recipient of list) {
            const address = addressFor(recipient, channel);
            if (!address) {
                blocked.push({ recipient, reason: 'No address on this channel.' });
            } else if (withdrawn.has(address.toLowerCase())) {
                blocked.push({ recipient, reason: 'They asked this sender to stop on this channel.' });
            } else {
                eligible.push(recipient);
            }
        }
    }

    const total = list.length;
    const noun = total === 1 ? 'recipient' : 'recipients';
    const where = channelName(channel);

    const summary = blocked.length === 0
        ? `All ${total} ${noun} can be reached on ${where}.`
        : `${eligible.length} of ${total} ${noun} can be reached on ${where}. `
          + `${blocked.length} ${blocked.length === 1 ? 'is' : 'are'} blocked.`;

    return {
        eligible,
        blocked,
        total,
        eligibleCount: eligible.length,
        blockedCount: blocked.length,
        summary,
    };
}
